import { useState, useCallback, useEffect, useRef } from 'react';

/**
 * Storage key pattern matching useTablePreferences for compatibility
 */
const getStorageKey = (tableId: string) => `table-prefs-${tableId}`;

interface SearchPreference {
  query: string;
  column: string;
}

interface UseSearchPreferenceOptions {
  tableId: string;
  defaultColumn?: string;
}

interface UseSearchPreferenceReturn {
  searchQuery: string;
  searchColumn: string;
  setSearchQuery: (query: string) => void;
  setSearchColumn: (column: string) => void;
  clearSearch: () => void;
}

/**
 * Load search preference from localStorage
 * Compatible with useTablePreferences storage format
 */
function loadSearchPreference(
  tableId: string,
  defaultColumn: string
): SearchPreference {
  try {
    const stored = localStorage.getItem(getStorageKey(tableId));
    if (!stored) {
      return { query: '', column: defaultColumn };
    }

    const parsed = JSON.parse(stored);
    if (parsed.search) {
      return {
        query: parsed.search.query || '',
        column: parsed.search.column || defaultColumn,
      };
    }

    return { query: '', column: defaultColumn };
  } catch (e) {
    console.warn(`Failed to load search preference for ${tableId}:`, e);
    return { query: '', column: defaultColumn };
  }
}

/**
 * Save search preference to localStorage
 * Compatible with useTablePreferences storage format
 */
function saveSearchPreference(tableId: string, search: SearchPreference): void {
  try {
    const key = getStorageKey(tableId);
    const existing = localStorage.getItem(key);
    let data: any = {};

    if (existing) {
      try {
        data = JSON.parse(existing);
      } catch {
        data = {};
      }
    }

    // Update only the search field, preserving other preferences
    data.search = { query: search.query, column: search.column };

    localStorage.setItem(key, JSON.stringify(data));
  } catch (e) {
    console.warn(`Failed to save search preference for ${tableId}:`, e);
  }
}

/**
 * Hook for managing search preference with localStorage persistence.
 * Use this when you want search state to persist across page navigation.
 *
 * Compatible with useTablePreferences storage format.
 *
 * @example
 * ```tsx
 * const { searchQuery, searchColumn, setSearchQuery, setSearchColumn } = useSearchPreference({
 *   tableId: 'cars-table-all',
 *   defaultColumn: 'chs_no'
 * });
 * ```
 */
export function useSearchPreference({
  tableId,
  defaultColumn = '',
}: UseSearchPreferenceOptions): UseSearchPreferenceReturn {
  // Initialize state from localStorage
  const [search, setSearch] = useState<SearchPreference>(() =>
    loadSearchPreference(tableId, defaultColumn)
  );

  // Track if this is the initial mount to avoid unnecessary saves
  const isInitialMount = useRef(true);
  const prevTableIdRef = useRef(tableId);

  // Re-load from localStorage when tableId changes
  useEffect(() => {
    if (prevTableIdRef.current !== tableId) {
      prevTableIdRef.current = tableId;
      setSearch(loadSearchPreference(tableId, defaultColumn));
    }
  }, [tableId, defaultColumn]);

  // Persist to localStorage when search changes (skip initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    saveSearchPreference(tableId, search);
  }, [tableId, search]);

  // Handle search query changes
  const setSearchQuery = useCallback((query: string) => {
    setSearch(prev => ({ ...prev, query }));
  }, []);

  // Handle search column changes
  const setSearchColumn = useCallback((column: string) => {
    setSearch(prev => ({ ...prev, column }));
  }, []);

  // Clear search (reset to defaults)
  const clearSearch = useCallback(() => {
    setSearch({ query: '', column: defaultColumn });
  }, [defaultColumn]);

  return {
    searchQuery: search.query,
    searchColumn: search.column,
    setSearchQuery,
    setSearchColumn,
    clearSearch,
  };
}
